import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { M3ButtonComponent } from '@shared/button';
import { AuthStore } from '@core/auth';

@Component({
  selector: 'app-no-roles-assigned',
  standalone: true,
  imports: [MatIconModule, M3ButtonComponent],
  template: `
    <div class="no-roles-wrapper">
      <div class="no-roles-content">
        <div class="no-roles-icon-container">
          <mat-icon class="no-roles-icon">lock</mat-icon>
        </div>

        <div class="no-roles-text">
          <h1 class="no-roles-title">{{ title }}</h1>
          <p class="no-roles-message">
            {{ messagePrefix }}
            <a class="no-roles-link" [href]="'mailto:' + helpdeskEmail">{{
              helpdeskEmail
            }}</a>
            {{ messageSuffix }}
          </p>
        </div>

        <div class="no-roles-actions">
          <app-m3-button
            variant="filled"
            shape="round"
            iconLeft="logout"
            [ariaLabel]="logoutAriaLabel"
            (clicked)="onLogout()"
          >
            {{ logoutLabel }}
          </app-m3-button>
        </div>
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      :host {
        display: block;
        height: 100%;
      }

      .no-roles-wrapper {
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 100%;
        padding: var(--md-sys-spacing-extra-extra-large)
          var(--md-sys-spacing-extra-large);
        background: var(--md-sys-color-surface);
      }

      .no-roles-content {
        max-width: 480px;
        width: 100%;
        text-align: center;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: var(--md-sys-spacing-extra-large);
        background: var(--md-sys-color-surface-container);
        padding: var(--md-sys-spacing-extra-large-increased);
        border-radius: var(--md-sys-shape-corner-extra-large);
        border: 1px solid var(--md-sys-color-outline-variant);
      }

      .no-roles-icon-container {
        width: 96px;
        height: 96px;
        border-radius: var(--md-sys-shape-corner-full);
        background: var(--md-sys-color-secondary-container);
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .no-roles-icon {
        font-size: 48px;
        width: 48px;
        height: 48px;
        color: var(--md-sys-color-on-secondary-container);
      }

      .no-roles-text {
        display: flex;
        flex-direction: column;
        gap: var(--md-sys-spacing-medium);
      }

      .no-roles-title {
        font-size: 28px;
        font-weight: 600;
        color: var(--md-sys-color-on-surface);
        margin: 0;
      }

      .no-roles-message {
        font-size: 16px;
        color: var(--md-sys-color-on-surface-variant);
        margin: 0;
        line-height: 1.5;
      }

      .no-roles-link {
        color: var(--md-sys-color-primary);
        text-decoration: none;
      }

      .no-roles-link:hover {
        text-decoration: underline;
      }

      .no-roles-actions {
        display: flex;
        align-items: center;
        justify-content: center;
      }
    `,
  ],
})
export class NoRolesAssignedComponent {
  private authStore = inject(AuthStore);

  protected readonly helpdeskEmail = 'helpdesk@combi.systems';
  protected readonly title = $localize`:@@noRoles.title:Kein Zugriff`;
  protected readonly messagePrefix = $localize`:@@noRoles.messagePrefix:Ihnen wurden keine Rolle zugewiesen. Melden Sie sich bitte beim Helpdesk unter`;
  protected readonly messageSuffix = $localize`:@@noRoles.messageSuffix:zur Zuweisung.`;
  protected readonly logoutLabel = $localize`:@@noRoles.logout:Abmelden`;
  protected readonly logoutAriaLabel = $localize`:@@noRoles.logoutAria:Abmelden`;

  onLogout(): void {
    this.authStore.logout();
  }
}
